(() => {
  if (customElements.get('izy-locker-checkout')) return;
  const keys = ['boxnow_locker_id', 'boxnow_locker_name', 'boxnow_locker_address', 'boxnow_locker_country'];
  const lockerFrom = cart => {
    const a = cart.attributes || {};
    return { id: String(a.boxnow_locker_id || ''), name: String(a.boxnow_locker_name || ''), address: String(a.boxnow_locker_address || ''), country: String(a.boxnow_locker_country || '').toUpperCase() };
  };
  const valid = locker => !!(locker?.id && locker.name && locker.address && ['GR', 'CY'].includes(locker.country));
  class LockerCheckout extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      this.checkout = this.form?.querySelector('[name="checkout"]');
      if (!this.checkout) return;
      this.events = new AbortController();
      this.panel = this.querySelector('[data-locker-panel]');
      this.status = this.querySelector('[data-locker-status]');
      this.country = this.querySelector('[data-locker-country]');
      this.countryChoice = this.querySelector('[data-locker-country-choice]');
      this.chooseButton = this.querySelector('[data-locker-action="choose"]');
      this.withoutButton = this.querySelector('[data-locker-action="without"]');
      this.selection = this.querySelector('[data-locker-selection]');
      this.instructions = this.querySelector('.izy-locker-checkout__instructions');
      this.selected = { id: this.dataset.savedId, name: this.dataset.savedName, address: this.dataset.savedAddress, country: (this.dataset.savedCountry || '').toUpperCase() };
      if (!valid(this.selected)) this.selected = null;
      this.allowSubmit = false;
      this.choiceUnlockedAt = 0;
      this.country.value = '';
      this.checkout.setAttribute('aria-controls', this.panel.id);
      this.setPhase(this.selected ? 'ready' : 'closed');
      this.form.addEventListener('submit', event => {
        if (event.submitter !== this.checkout) return;
        if (this.allowSubmit) { this.allowSubmit = false; return; }
        event.preventDefault();
        if (this.phase === 'closed') this.show();
        else if (['ready', 'country'].includes(this.phase) && valid(this.selected)) this.proceedWithLocker();
      }, { signal: this.events.signal });
      this.addEventListener('click', event => {
        const action = event.target.closest('[data-locker-action]')?.dataset.lockerAction;
        // A second pointer tap from opening Checkout must not confirm a newly revealed action.
        if (action === 'without' && (event.detail > 1 || (event.detail > 0 && performance.now() < this.choiceUnlockedAt))) {
          event.preventDefault(); return;
        }
        if (action === 'choose') this.chooseCountry();
        if (action === 'without') this.without();
        if (action === 'cancel') this.close();
      }, { signal: this.events.signal });
      this.country.addEventListener('change', () => {
        if (this.phase === 'country' && ['GR', 'CY'].includes(this.country.value)) this.openMap();
      }, { signal: this.events.signal });
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.phase !== 'closed') { event.preventDefault(); event.stopPropagation(); this.close(); }
      }, { signal: this.events.signal });
      window.addEventListener('pageshow', event => { if (event.persisted) this.close(false); }, { signal: this.events.signal });
      this.closest('cart-drawer')?.addEventListener('dialog:after-hide', () => this.close(false), { signal: this.events.signal });
    }
    disconnectedCallback() { this.cancelOperation(); this.events?.abort(); }
    message(key) { this.status.textContent = this.dataset[key] || ''; }
    setPhase(phase) {
      if (this.phase === 'opening' && phase === 'choice') this.choiceUnlockedAt = performance.now() + 450;
      this.phase = phase;
      const pending = ['opening', 'loading', 'map', 'saving'].includes(phase);
      this.panel.hidden = phase === 'closed';
      this.panel.setAttribute('aria-busy', String(pending));
      this.countryChoice.hidden = phase !== 'country';
      this.chooseButton.setAttribute('aria-expanded', String(phase === 'country'));
      this.querySelectorAll('[data-locker-action]:not([data-locker-action="cancel"]), select').forEach(el => { el.disabled = pending; });
      const selected = valid(this.selected);
      this.selection.hidden = !selected;
      this.instructions.hidden = selected;
      this.selection.textContent = selected ? `${this.dataset.selected || ''} ${this.selected.name} · ${this.selected.address} (${this.selected.country})` : '';
      this.withoutButton.hidden = selected;
      this.checkout.hidden = phase !== 'closed' && !(selected && ['ready', 'country'].includes(phase));
      this.checkout.setAttribute('aria-expanded', String(phase !== 'closed'));
    }
    async show() {
      const signal = this.startOperation();
      this.setPhase('opening'); this.message('saving');
      try {
        const locker = lockerFrom(await this.readCart(signal));
        this.selected = valid(locker) ? locker : null;
        this.status.textContent = ''; this.setPhase(this.selected ? 'ready' : 'choice'); this.chooseButton.focus();
      } catch { this.fail(signal); }
    }
    chooseCountry() {
      if (!['choice', 'ready', 'country'].includes(this.phase)) return;
      this.status.textContent = '';
      if (this.phase === 'country') { this.setPhase(valid(this.selected) ? 'ready' : 'choice'); return; }
      this.country.value = ''; this.setPhase('country'); this.country.focus();
    }
    cancelOperation() {
      this.operation?.abort(); clearTimeout(this.mapTimeout);
      if (this.restoreCallback) this.restoreCallback();
      this.restoreCallback = null;
    }
    close(focus = true) {
      const hadMap = ['map', 'saving'].includes(this.phase);
      this.cancelOperation(); this.status.textContent = ''; this.setPhase('closed');
      if (hadMap) document.getElementById('tp-box-now-dialog')?.close();
      if (focus && this.isConnected) this.checkout.focus();
    }
    startOperation() {
      this.cancelOperation(); this.operation = new AbortController(); return this.operation.signal;
    }
    async cartRequest(path, signal, attributes) {
      const deadline = new AbortController();
      const abort = () => deadline.abort();
      signal.addEventListener('abort', abort, { once: true });
      const timeout = setTimeout(abort, 8000);
      try {
        if (signal.aborted) throw new Error('Cancelled');
        const response = await fetch(`${window.Shopify.routes.root}${path}`, {
          method: attributes ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: deadline.signal,
          ...(attributes ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes }) } : {})
        });
        if (!response.ok) throw new Error('Cart request failed');
        const cart = await response.json();
        if (!cart.item_count || signal.aborted || !this.isConnected) throw new Error('Cart unavailable');
        return cart;
      } finally { clearTimeout(timeout); signal.removeEventListener('abort', abort); }
    }
    readCart(signal) { return this.cartRequest('cart.js', signal); }
    async clearLocker(signal) {
      const cart = await this.readCart(signal);
      if (keys.some(key => cart.attributes?.[key])) {
        await this.cartRequest('cart/update.js', signal, Object.fromEntries(keys.map(key => [key, ''])));
        const checked = await this.readCart(signal);
        if (keys.some(key => checked.attributes?.[key])) throw new Error('Locker not cleared');
      }
    }
    fail(signal) {
      if (signal.aborted || !this.isConnected) return;
      this.cancelOperation(); this.selected = null; this.setPhase('choice');
      this.message('error'); this.chooseButton.focus();
    }
    async openMap() {
      if (this.phase !== 'country') return;
      if (!['GR', 'CY'].includes(this.country.value)) { this.message('countryRequired'); this.country.focus(); return; }
      const selectedCountry = this.country.value;
      const signal = this.startOperation(); this.setPhase('loading'); this.message('loading');
      try {
        const button = document.getElementById('tp-box-now-drawer-lockers-button');
        const config = window._bn_map_widget_config;
        const providerCountry = document.querySelector('.tp-box-now-drawer-locker-selection__country-select');
        if (!button || !config || config.buttonSelector !== '#tp-box-now-drawer-lockers-button' || typeof config.afterSelect !== 'function') throw new Error('Map not available');
        if (providerCountry) {
          const option = [...providerCountry.options].find(option => option.value.toUpperCase() === selectedCountry);
          if (!option) throw new Error('Country not available');
          providerCountry.value = option.value;
        } else if (document.getElementById('tp-box-now-drawer-lockers-button-data')?.dataset.countrySelector?.toUpperCase() !== selectedCountry) throw new Error('Country unavailable');
        await this.clearLocker(signal);
        if (signal.aborted) return;
        this.selected = null; this.setPhase('loading');
        const original = config.afterSelect;
        const wrapper = (...args) => {
          if (signal.aborted) return original.apply(config, args);
          this.setPhase('saving'); this.message('saving'); clearTimeout(this.mapTimeout);
          this.restoreCallback?.(); this.restoreCallback = null;
          try { original.apply(config, args); } catch { this.fail(signal); return; }
          const id = String(args[0]?.boxnowLockerId || '');
          if (!id) { this.fail(signal); return; }
          this.waitForSaved(id, selectedCountry, signal).catch(() => this.fail(signal));
        };
        config.afterSelect = wrapper;
        this.restoreCallback = () => { if (config.afterSelect === wrapper) config.afterSelect = original; };
        this.setPhase('map'); button.click();
        const dialog = document.getElementById('tp-box-now-dialog');
        if (!dialog?.open) throw new Error('Map did not open');
        dialog.addEventListener('close', () => {
          if (this.phase === 'map' && !signal.aborted) { this.cancelOperation(); this.setPhase('choice'); this.status.textContent = ''; this.chooseButton.focus(); }
        }, { once: true, signal });
        this.mapTimeout = setTimeout(() => { if (this.phase === 'map') { this.fail(signal); dialog.close(); } }, 180000);
      } catch { this.fail(signal); }
    }
    async waitForSaved(id, country, signal) {
      for (let attempt = 0; attempt < 20; attempt++) {
        const locker = lockerFrom(await this.readCart(signal));
        if (locker.id === id && locker.country === country && valid(locker)) {
          this.selected = locker; this.status.textContent = ''; this.setPhase('ready'); this.checkout.focus(); return;
        }
        await new Promise(resolve => setTimeout(resolve, 250));
        if (signal.aborted) return;
      }
      throw new Error('Locker persistence not confirmed');
    }
    async without() {
      if (!['choice', 'country'].includes(this.phase) || valid(this.selected)) return;
      const signal = this.startOperation(); this.setPhase('saving'); this.message('saving');
      try { await this.clearLocker(signal); if (!signal.aborted) this.submitCheckout(); } catch { this.fail(signal); }
    }
    async proceedWithLocker() {
      if (!['ready', 'country'].includes(this.phase) || !valid(this.selected)) return;
      const signal = this.startOperation(); this.setPhase('saving'); this.message('saving');
      try {
        const actual = lockerFrom(await this.readCart(signal));
        if (actual.id !== this.selected.id || actual.country !== this.selected.country || !valid(actual)) throw new Error('Locker changed');
        if (!signal.aborted) this.submitCheckout();
      } catch { this.fail(signal); }
    }
    submitCheckout() {
      this.cancelOperation(); this.allowSubmit = true; this.phase = 'checkout';
      this.checkout.hidden = false; this.form.requestSubmit(this.checkout);
    }
  }
  customElements.define('izy-locker-checkout', LockerCheckout);
})();
